import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SeoHead from '../components/SeoHead'
import { getCmsPage } from '../lib/cms'
import { getOptimizedImageUrl } from '../lib/utils'

export default function AboutPage() {
  const [pageData, setPageData] = useState(null)

  useEffect(() => {
    let isMounted = true
    getCmsPage('om-mig').then((page) => {
      if (isMounted) setPageData(page)
    })
    return () => {
      isMounted = false
    }
  }, [])

  const ampImage = 'https://assets.auxodev.dk/assets/d6e713fb-6411-4075-ab6a-5936b7a50d5a/2dee4a-d78699cbcd994ac0866c1032fa7a7f83-mv2-d-4608-3072-s-4-2.jpg'
  const roomImage = 'https://assets.auxodev.dk/assets/d6e713fb-6411-4075-ab6a-5936b7a50d5a/2dee4a-75a790a4e93b4a67a6981ddeae1ed7f4-mv2.jpg'

  return (
    <div className="bg-[#121214] text-[#F5F3EF]">
      <SeoHead
        title={pageData?.seo_title || "Om Jens & JARCon | Passion for Teknik, Lyd & Håndværk"}
        description={
          pageData?.meta_description ||
          "Mød Jens bag JARCon ApS og JARC Audio. Mange års passion for elektronik, præcisionshåndværk og kompromisløs lydgengivelse i Sydals."
        }
        ogImage={ampImage}
        canonicalUrl="https://www.jarcon.dk/om-mig"
      />

      {/* Hero */}
      <section className="relative border-b border-white/10 bg-[#0E0E10] py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-xs uppercase tracking-widest text-[#C87D46] font-medium mb-4">
            Mennesket & Filosofien
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#F5F3EF]">
            Om mig & JARCon
          </h1>
          <p className="mt-4 font-serif italic text-lg sm:text-2xl text-[#E5E2DB]">
            “Passion for teknik, håndværk og kompromisløs lydgengivelse”
          </p>
        </div>
      </section>

      {/* Main story */}
      <section className="py-16 sm:py-24 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Story text */}
            <div className="lg:col-span-7 space-y-6 text-sm sm:text-base text-[#A8A49C] leading-relaxed">
              <h2 className="font-serif text-2xl sm:text-3xl text-[#F5F3EF]">
                En livslang kærlighed til lyd og konstruktion
              </h2>
              <p>
                HiFi og DIY HiFi har været en passion og hobby for mig i mange år. Gennem tiden har jeg beskæftiget mig med konstruktion af pladespillere, kabler og tilbehør, men det er primært højtalerbyg, der har min dybeste interesse.
              </p>
              <p>
                Det er blevet til mange spændende højtaler konstruktioner gennem årene. Hver konstruktion har givet værdifulde erfaringer om kabinetakustik, delefiltre, enheder og samspillet med rummet.
              </p>
              <div className="rounded-sm border-l-2 border-[#C87D46] bg-[#18181C] p-5 my-6 text-[#F5F3EF]">
                <p className="font-serif italic text-base sm:text-lg">
                  “Målet er at skabe en højtaler, som kan gengive al slags musik med nerve og nærvær, hvor lytteren glemmer at man lytter til HiFi, men føler sig omslugt at musikken.”
                </p>
              </div>
              <p>
                I dag driver jeg JARCon ApS med base på Als i Sønderjylland. Her kombineres rådgivning, målinger og skræddersyet lyd med det uformelle møde over en frisk kop kaffe.
              </p>
            </div>

            {/* Imagery */}
            <div className="lg:col-span-5 space-y-6">
              <div className="rounded-sm border border-white/10 bg-[#18181C] overflow-hidden shadow-2xl">
                <img
                  src={getOptimizedImageUrl(ampImage, { width: 800, quality: 85 })}
                  alt="Mono rørudgangstrin med 4 stk. KT150 rør"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="p-4 bg-[#141418] text-xs text-[#E5E2DB]">
                  Mono rørudgangstrin med 4 stk. KT150 rør og håndviklede C-kerne trafoer
                </div>
              </div>

              <div className="rounded-sm border border-white/10 bg-[#18181C] p-6 text-xs text-[#A8A49C] space-y-2">
                <div className="font-serif text-sm text-[#F5F3EF]">Virksomhedsfakta</div>
                <p><strong>Firmanavn:</strong> JARCon ApS</p>
                <p><strong>CVR:</strong> 39150255</p>
                <p><strong>Adresse / Lytterum:</strong> Toftehøj 21, 6470 Sydals</p>
                <p><strong>Produktionsadresse:</strong> Huholt 1A, 6400 Sønderborg</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lytterum & Invitation */}
      <section className="py-16 sm:py-24 bg-[#0E0E10] text-center border-b border-white/10">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#F5F3EF]">
            Oplev lytterummet på Sydals
          </h2>
          <p className="mt-4 text-sm sm:text-base text-[#A8A49C] max-w-2xl mx-auto leading-relaxed">
            Du er velkommen til at komme forbi og lytte til mit setup med DIY højtalere, pladespiller og rørforstærker. Medbring gerne din egen favoritmusik på vinyl eller digitalt format.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-sm bg-[#C87D46] px-7 py-3 text-sm font-medium text-white hover:bg-[#D98E58] transition-colors"
            >
              <i className="fa-solid fa-mug-hot text-xs" aria-hidden="true" />
              <span>Aftal et besøg på Toftehøj 21</span>
            </Link>
            <Link
              to="/hifi"
              className="inline-flex items-center gap-2 rounded-sm border border-white/20 px-7 py-3 text-sm font-medium text-[#F5F3EF] hover:bg-white/5 transition-colors"
            >
              <span>Se HiFi setuppet</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
